import { translateInline } from '@/globals/translate/translateInline';

export type QuestionTranslation = {
	id: string;
	title: ReturnType<typeof translateInline>;
	shortTitle: ReturnType<typeof translateInline>;
	help: ReturnType<typeof translateInline>;
	required: boolean;
	answers: Array<{
		id: string;
		text: {
			single: ReturnType<typeof translateInline>;
			plural?: ReturnType<typeof translateInline>;
		};
	}>;
};

export const questionTranslations: Array<QuestionTranslation> = [
	{
		id: 'at',
		title: translateInline({
			en: 'Do you want to start a business alone or with others?',
			nl: 'Wilt u alleen of samen een bedrijf beginnen?',
		}),
		shortTitle: translateInline({
			en: 'Alone or together',
			nl: 'Alleen of samen',
		}),
		help: translateInline({
			en: 'There are different types of collaboration: each legal structure comes with its own conditions and consequences. For instance, if you alone are liable for the entire company debt, or only for part of it; or, how easy it is to get out of the collaboration, without endangering its continued existence.',
			nl: 'U kunt op verschillende manieren samenwerken. Elke rechtsvorm heeft zijn eigen voorwaarden en consequenties. Bijvoorbeeld of u alleen aansprakelijk bent voor uw eigen schulden of ook voor die van anderen. En of u makkelijk uit de samenwerking kunt stappen zonder het bedrijf in gevaar te brengen.',
		}),
		required: true,
		answers: [
			{
				id: 'a',
				text: {
					single: translateInline({
						en: 'I am starting a business on my own',
						nl: 'Ik ga alleen ondernemen',
					}),
				},
			},
			{
				id: 'p',
				text: {
					single: translateInline({
						en: 'I am starting a business with a partner',
						nl: 'Ik ga ondernemen met een compagnon',
					}),
				},
			},
			{
				id: 'gp',
				text: {
					single: translateInline({
						en: 'I am starting a business with a group of partners',
						nl: 'Ik ga ondernemen met een grote groep compagnons',
					}),
				},
			},
			{
				id: 'lp',
				text: {
					single: translateInline({
						en: 'I am starting a business with my life-partner',
						nl: 'Ik ga ondernemen met mijn levenspartner',
					}),
				},
			},
			{
				id: 'ci',
				text: {
					single: translateInline({
						en: 'I want to collaborate on an independent basis',
						nl: 'Ik wil onafhankelijk samenwerken',
					}),
				},
			},
			{
				id: 'n',
				text: {
					single: translateInline({
						en: 'I want to collaborate under a shared name',
						nl: 'Wij willen samenwerken onder één naam',
					}),
				},
			},
		],
	},
	{
		id: 'a',
		title: translateInline({
			en: 'What is your ambition?',
			nl: 'Wat is uw doel?',
		}),
		shortTitle: translateInline({
			en: 'Ambition',
			nl: 'Ambitie',
		}),
		help: translateInline({
			en: 'Some legal structures require the services of an actuary when starting out. And some legal structures come with administrative obligations, like having to produce and deposit annual accounts at the KVK. If your business risk increases, for instance if your company is growing, a legal structure with legal personality is an option worth looking into. It will limit the risk you run personally.',
			nl: "Sommige rechtsvormen kunt u alleen oprichten met tussenkomst van een notaris. Daarnaast kunt u te maken krijgen met verplichtingen rond de administratie, zoals het opstellen en deponeren van jaarstukken bij KVK. Als door groei uw risico's toenemen, is een rechtsvorm met rechtspersoonlijkheid handig. Uw persoonlijk risico blijft dan beperkt.",
		}),
		required: false,
		answers: [
			{
				id: 'ap',
				text: {
					single: translateInline({
						en: 'I want to start as soon as possible, with a minimum of administrative obligations',
						nl: 'Ik wil snel starten met weinig administratieve verplichtingen',
					}),
					plural: translateInline({
						en: 'We want to start as soon as possible, with a minimum of administrative obligations',
						nl: 'Wij willen snel starten met weinig administratieve verplichtingen',
					}),
				},
			},
			{
				id: 'eq',
				text: {
					single: translateInline({
						en: 'I want my business to grow quickly',
						nl: 'Ik wil mijn bedrijf snel laten groeien',
					}),
					plural: translateInline({
						en: 'We want our business to grow quickly',
						nl: 'Wij willen ons bedrijf snel laten groeien',
					}),
				},
			},
		],
	},
	{
		id: 'br',
		title: translateInline({
			en: 'Do you have enough funds to cover any business risks?',
			nl: "Heeft u genoeg geld om bedrijfsrisico's te betalen?",
		}),
		shortTitle: translateInline({
			en: 'Business risk',
			nl: 'Bedrijfsrisico',
		}),
		help: translateInline({
			en: "Who is liable for business risks if things don't work out depends on your legal structure. If you choose a legal structure with legal personality, such as the private limited company, the company will be liable for any debts. If you choose a legal structure without legal personality, such as a commercial partnership, you will be personally liable, with your private capital.",
			nl: 'Uw rechtsvorm bepaalt wie het bedrijfsrisico opvangt als het misgaat. Bij ondernemingsvormen met rechtspersoonlijkheid, zoals de bv, is het bedrijf aansprakelijk voor eventuele schulden. Bij ondernemingsvormen zonder rechtspersoonlijkheid bent u met uw eigen geld aansprakelijk.',
		}),
		required: false,
		answers: [
			{
				id: 'ef',
				text: {
					single: translateInline({
						en: 'I have enough funds to cover my business risks',
						nl: 'Ik kan mijn bedrijfsrisico zelf betalen',
					}),
					plural: translateInline({
						en: 'We have enough funds to cover our business risks',
						nl: 'Wij kunnen ons bedrijfsrisico zelf betalen',
					}),
				},
			},
			{
				id: 'nef',
				text: {
					single: translateInline({
						en: 'I do not have enough funds to cover my business risks',
						nl: 'Mijn bedrijfsrisico is groter dan ik zelf kan betalen',
					}),
					plural: translateInline({
						en: 'We do not have enough funds to cover our business risks',
						nl: 'Ons bedrijfsrisico is groter dan wij zelf kunnen betalen',
					}),
				},
			},
			{
				id: 'ef2',
				text: {
					single: translateInline({
						en: 'I have access to external funding',
						nl: 'Ik gebruik vreemd vermogen',
					}),
					plural: translateInline({
						en: 'We have access to external funding',
						nl: 'Wij gebruiken vreemd vermogen',
					}),
				},
			},
			{
				id: 'pc',
				text: {
					single: translateInline({
						en: 'I am using only private capital',
						nl: 'Ik gebruik eigen vermogen',
					}),
					plural: translateInline({
						en: 'We are using only private capital',
						nl: 'Wij gebruiken eigen vermogen',
					}),
				},
			},
		],
	},
	{
		id: 'pt',
		title: translateInline({
			en: 'What amount of profit do you expect to make?',
			nl: 'Hoeveel winst verwacht u te maken?',
		}),
		shortTitle: translateInline({
			en: 'Profit',
			nl: 'Winst',
		}),
		help: translateInline({
			en: 'Which taxes you pay depends on your legal structure. Your company has to pay corporate tax if it has a legal structure with legal personality, and income tax if it does not have a legal structure with legal personality. If your profit increases, paying corporate tax becomes more advantageous. There are also differences in what you can deduct.',
			nl: 'Uw rechtsvorm bepaalt welke belastingen u betaalt. Bij een ondernemingsvorm  met rechtspersoonlijkheid betaalt u vennootschapsbelasting. Bij een rechtsvorm zonder rechtspersoonlijkheid betaalt u inkomstenbelasting. Als uw winst toeneemt, dan is het voordeliger om vennootschapsbelasting te betalen. Ook verschillen de aftrekposten.',
		}),
		required: false,
		answers: [
			{
				id: 'hp',
				text: {
					single: translateInline({
						en: 'I expect to make a profit of less than €150,000',
						nl: 'Ik verwacht een winst van minder dan €150.000',
					}),
					plural: translateInline({
						en: 'We expect to make a profit of less than €150,000',
						nl: 'Wij verwachten een winst van minder dan €150.000',
					}),
				},
			},
			{
				id: 'lp',
				text: {
					single: translateInline({
						en: 'I expect to make a profit of over €150,000',
						nl: 'Ik verwacht een winst van meer dan €150.000',
					}),
					plural: translateInline({
						en: 'We expect to make a profit of over €150,000',
						nl: 'Wij verwachten een winst van meer dan €150.000',
					}),
				},
			},
		],
	},
	{
		id: 'hs',
		title: translateInline({
			en: 'Will you be hiring staff?',
			nl: 'Gaat u personeel aannemen?',
		}),
		shortTitle: translateInline({
			en: 'Staff',
			nl: 'Personeel',
		}),
		help: translateInline({
			en: "You can hire staff no matter what your legal structure is. Hiring staff does involve risks. You have obligations toward your staff, that may be hard to meet if your company does not do well. Depending on your company's legal structure, you may be personally liable for these risks.",
			nl: "U kunt met elke rechtsvorm personeel aannemen. Als u personeel aanneemt, brengt dit risico's met zich mee. Als het slecht gaat met uw bedrijf, heeft u verplichtingen naar uw personeel. De ondernemingsvorm bepaalt of u persoonlijk aansprakelijk bent voor dit risico of niet.",
		}),
		required: false,
		answers: [
			{
				id: 'ns',
				text: {
					single: translateInline({
						en: 'I will not be hiring staff (yet)',
						nl: 'Ik werk zonder personeel',
					}),
					plural: translateInline({
						en: 'We will not be hiring staff (yet)',
						nl: 'Wij werken zonder personeel',
					}),
				},
			},
			{
				id: 'ws',
				text: {
					single: translateInline({
						en: 'I will be hiring staff',
						nl: 'Ik werk met personeel',
					}),
					plural: translateInline({
						en: 'We will be hiring staff',
						nl: 'Wij werken met personeel',
					}),
				},
			},
		],
	},
];

export type ResultTranslations = {
	resultId: string;
	title: ReturnType<typeof translateInline>;
	articlePath: ReturnType<typeof translateInline>;
	registerLink: ReturnType<typeof translateInline>;
	scoreResult: Array<{
		questionId: string;
		answers: Array<{
			answerId: string;
			score: number;
		}>;
	}>;
	description: Array<{
		text: ReturnType<typeof translateInline>;
	}>;
};

export const results: Array<ResultTranslations> = [
	{
		resultId: 'a',
		title: translateInline({
			en: 'Sole proprietorship (eenmanszaak)',
			nl: 'Eenmanszaak',
		}),
		articlePath: translateInline({
			en: '/running-your-business/legal-forms-and-governance/sole-trader-or-sole-proprietorship-in-the-netherlands/',
			nl: '/bedrijfsvoering/rechtsvormen-en-organisatie/de-eenmanszaak/',
		}),
		registerLink: translateInline({
			en: 'https://www.kvk.nl/en/registration/registering-a-dutch-eenmanszaak-and-making-an-appointment/',
			nl: 'https://www.kvk.nl/inschrijven/inschrijven-en-afspraak-maken-eenmanszaak/',
		}),
		scoreResult: [
			{
				questionId: 'at',
				answers: [
					{ answerId: 'a', score: 20 },
					{ answerId: 'p', score: -100 },
					{ answerId: 'gp', score: -100 },
					{ answerId: 'lp', score: -100 },
					{ answerId: 'ci', score: 20 },
					{ answerId: 'n', score: -100 },
				],
			},
			{
				questionId: 'a',
				answers: [
					{ answerId: 'ap', score: 20 },
					{ answerId: 'eq', score: 10 },
				],
			},
			{
				questionId: 'br',
				answers: [
					{ answerId: 'ef', score: 10 },
					{ answerId: 'nef', score: -20 },
					{ answerId: 'ef2', score: -10 },
					{ answerId: 'pc', score: 20 },
				],
			},
			{
				questionId: 'pt',
				answers: [
					{ answerId: 'hp', score: 20 },
					{ answerId: 'lp', score: -10 },
				],
			},
			{
				questionId: 'hs',
				answers: [
					{ answerId: 'ns', score: 20 },
					{ answerId: 'ws', score: 10 },
				],
			},
		],
		description: [
			{
				text: translateInline({
					en: 'starting is quick and easy',
					nl: 'snel en eenvoudig op te richten',
				}),
			},
			{
				text: translateInline({
					en: 'you can only register one sole proprietorship',
					nl: 'maximaal één eenmanszaak',
				}),
			},
			{
				text: translateInline({
					en: 'you can find the current [registration fee](https://www.kvk.nl/en/registration/registration-fee/) on the KVK website',
					nl: 'huidige [inschrijfvergoeding](https://www.kvk.nl/inschrijven/inschrijfvergoeding/) vindt u op kvk.nl',
				}),
			},
			{
				text: translateInline({
					en: 'personally liable for company debts',
					nl: 'met eigen geld aansprakelijk voor eventuele schulden',
				}),
			},
			{
				text: translateInline({
					en: 'possible tax relief schemes: private business ownership allowance, SME profit exemption, tax relief for new companies',
					nl: 'recht op zelfstandigenaftrek en mkb-winstvrijstelling, starters profiteren van startersaftrek',
				}),
			},
		],
	},
	{
		resultId: 'b',
		title: translateInline({
			en: 'Private limited company (BV)',
			nl: 'Besloten vennootschap (bv)',
		}),
		articlePath: translateInline({
			en: '/running-your-business/legal-forms-and-governance/private-limited-company-in-the-netherlands/',
			nl: '/bedrijfsvoering/rechtsvormen-en-organisatie/de-besloten-vennootschap-bv/',
		}),
		registerLink: translateInline({
			en: 'https://www.kvk.nl/en/registration/registering-a-dutch-bv-or-nv/',
			nl: 'https://www.kvk.nl/inschrijven/inschrijven-besloten-vennootschap-of-naamloze-vennootschap/',
		}),
		scoreResult: [
			{
				questionId: 'at',
				answers: [
					{ answerId: 'a', score: 20 },
					{ answerId: 'p', score: 20 },
					{ answerId: 'gp', score: 20 },
					{ answerId: 'lp', score: 20 },
					{ answerId: 'ci', score: 20 },
					{ answerId: 'n', score: 20 },
				],
			},
			{
				questionId: 'a',
				answers: [
					{ answerId: 'ap', score: 0 },
					{ answerId: 'eq', score: 20 },
				],
			},
			{
				questionId: 'br',
				answers: [
					{ answerId: 'ef', score: -10 },
					{ answerId: 'nef', score: 20 },
					{ answerId: 'ef2', score: 20 },
					{ answerId: 'pc', score: 15 },
				],
			},
			{
				questionId: 'pt',
				answers: [
					{ answerId: 'hp', score: -10 },
					{ answerId: 'lp', score: 20 },
				],
			},
			{
				questionId: 'hs',
				answers: [
					{ answerId: 'ns', score: 10 },
					{ answerId: 'ws', score: 20 },
				],
			},
		],
		description: [
			{
				text: translateInline({
					en: "setup using an actuary's services",
					nl: 'oprichten met tussenkomst van notaris',
				}),
			},
			{
				text: translateInline({
					en: 'cost of setup € 500-1000 actuary; € 600-1,800 annual administrative obligations',
					nl: 'kosten eenmalig € 500-1000 notaris, jaarlijks € 600-1800 administratie',
				}),
			},
			{
				text: translateInline({
					en: 'you can find the current [registration fee](https://www.kvk.nl/en/registration/registration-fee/) on the KVK website',
					nl: 'huidige [inschrijfvergoeding](https://www.kvk.nl/inschrijven/inschrijfvergoeding/) vindt u op kvk.nl',
				}),
			},

			{
				text: translateInline({
					en: 'not personally liable for any company debts',
					nl: 'niet persoonlijk aansprakelijk voor eventuele schulden',
				}),
			},
			{
				text: translateInline({
					en: 'capital divided into shares',
					nl: 'kapitaal verdeeld in aandelen',
				}),
			},
			{
				text: translateInline({
					en: 'tax burden lower when profits increase',
					nl: 'lagere belastingdruk bij hogere winsten',
				}),
			},
		],
	},
	{
		resultId: 'c',
		title: translateInline({
			en: 'Commercial partnership (VOF)',
			nl: 'Vennootschap onder firma (vof)',
		}),
		articlePath: translateInline({
			en: '/running-your-business/legal-forms-and-governance/general-partnership/',
			nl: '/bedrijfsvoering/rechtsvormen-en-organisatie/vennootschap-onder-firma-vof/',
		}),
		registerLink: translateInline({
			en: 'https://www.kvk.nl/en/registration/registering-and-making-an-appointment-for-a-vof/',
			nl: 'https://www.kvk.nl/inschrijven/inschrijven-en-afspraak-maken-vof/',
		}),
		scoreResult: [
			{
				questionId: 'at',
				answers: [
					{ answerId: 'a', score: -100 },
					{ answerId: 'p', score: 20 },
					{ answerId: 'gp', score: 10 },
					{ answerId: 'lp', score: 20 },
					{ answerId: 'ci', score: -100 },
					{ answerId: 'n', score: 20 },
				],
			},
			{
				questionId: 'a',
				answers: [
					{ answerId: 'ap', score: 20 },
					{ answerId: 'eq', score: 10 },
				],
			},
			{
				questionId: 'br',
				answers: [
					{ answerId: 'ef', score: 20 },
					{ answerId: 'nef', score: -15 },
					{ answerId: 'ef2', score: -10 },
					{ answerId: 'pc', score: 20 },
				],
			},
			{
				questionId: 'pt',
				answers: [
					{ answerId: 'hp', score: 20 },
					{ answerId: 'lp', score: -10 },
				],
			},
			{
				questionId: 'hs',
				answers: [
					{ answerId: 'ns', score: 10 },
					{ answerId: 'ws', score: 10 },
				],
			},
		],
		description: [
			{
				text: translateInline({
					en: 'starting is quick and easy',
					nl: 'snel en eenvoudig op te richten',
				}),
			},
			{
				text: translateInline({
					en: 'at least two partners',
					nl: 'minimaal twee vennoten',
				}),
			},
			{
				text: translateInline({
					en: 'you can find the current [registration fee](https://www.kvk.nl/en/registration/registration-fee/) on the KVK website',
					nl: 'huidige [inschrijfvergoeding](https://www.kvk.nl/inschrijven/inschrijfvergoeding/) vindt u op kvk.nl',
				}),
			},

			{
				text: translateInline({
					en: 'all partners are personally liable for company debts',
					nl: 'met eigen geld hoofdelijk aansprakelijk voor eventuele schulden',
				}),
			},
			{
				text: translateInline({
					en: 'possible tax relief schemes: private business ownership allowance, SME profit exemption, tax relief for new companies',
					nl: 'recht op zelfstandigenaftrek en mkb-winstvrijstelling, starters profiteren van startersaftrek',
				}),
			},
		],
	},
	{
		resultId: 'd',
		title: translateInline({
			en: 'Professional partnership (maatschap)',
			nl: 'Maatschap',
		}),
		articlePath: translateInline({
			en: '/running-your-business/legal-forms-and-governance/professional-partnership/',
			nl: '/bedrijfsvoering/rechtsvormen-en-organisatie/de-maatschap/',
		}),
		registerLink: translateInline({
			en: 'https://www.kvk.nl/en/registration/new-business-maatschap/',
			nl: 'https://www.kvk.nl/inschrijven/nieuw-bedrijf-maatschap/',
		}),
		scoreResult: [
			{
				questionId: 'at',
				answers: [
					{ answerId: 'a', score: -100 },
					{ answerId: 'p', score: 20 },
					{ answerId: 'gp', score: 10 },
					{ answerId: 'lp', score: 20 },
					{ answerId: 'ci', score: 10 },
					{ answerId: 'n', score: 10 },
				],
			},
			{
				questionId: 'a',
				answers: [
					{ answerId: 'ap', score: 20 },
					{ answerId: 'eq', score: 10 },
				],
			},
			{
				questionId: 'br',
				answers: [
					{ answerId: 'ef', score: 10 },
					{ answerId: 'nef', score: -10 },
					{ answerId: 'ef2', score: 0 },
					{ answerId: 'pc', score: 20 },
				],
			},
			{
				questionId: 'pt',
				answers: [
					{ answerId: 'hp', score: 20 },
					{ answerId: 'lp', score: -10 },
				],
			},
			{
				questionId: 'hs',
				answers: [
					{ answerId: 'ns', score: 10 },
					{ answerId: 'ws', score: 10 },
				],
			},
		],
		description: [
			{
				text: translateInline({
					en: 'starting is quick and easy',
					nl: 'snel en eenvoudig op te richten',
				}),
			},
			{
				text: translateInline({
					en: 'for specific (regulated) professions',
					nl: 'voor specifieke beroepsgroepen',
				}),
			},
			{
				text: translateInline({
					en: 'you can find the current [registration fee](https://www.kvk.nl/en/registration/registration-fee/) on the KVK website',
					nl: 'huidige [inschrijfvergoeding](https://www.kvk.nl/inschrijven/inschrijfvergoeding/) vindt u op kvk.nl',
				}),
			},

			{
				text: translateInline({
					en: 'agreement advisable (not required)',
					nl: 'overeenkomst niet verplicht/wel wenselijk',
				}),
			},
			{
				text: translateInline({
					en: 'collaborate under a shared name',
					nl: 'samenwerken onder een gemeenschappelijke naam',
				}),
			},
			{
				text: translateInline({
					en: 'liable with company funds first, then with personal capital for equal parts',
					nl: 'eerst met zakelijk vermogen aansprakelijk, dan met eigen geld voor gelijke delen',
				}),
			},
			{
				text: translateInline({
					en: 'possible tax relief schemes: private business ownership allowance, SME profit exemption, tax relief for new companies',
					nl: 'recht op zelfstandigenaftrek en mkb-winstvrijstelling, starters profiteren van startersaftrek',
				}),
			},
		],
	},
	{
		resultId: 'f',
		title: translateInline({
			en: 'Cooperative',
			nl: 'Coöperatie',
		}),
		articlePath: translateInline({
			en: '/running-your-business/legal-forms-and-governance/cooperative/',
			nl: '/bedrijfsvoering/rechtsvormen-en-organisatie/de-cooperatie/',
		}),
		registerLink: translateInline({
			en: 'https://www.kvk.nl/en/registration/registering-a-cooperative-or-mutual-insurance-society/',
			nl: 'https://www.kvk.nl/inschrijven/inschrijven-cooperatie-of-onderlinge-waarborgmaatschappij/',
		}),
		scoreResult: [
			{
				questionId: 'at',
				answers: [
					{ answerId: 'a', score: -100 },
					{ answerId: 'p', score: 20 },
					{ answerId: 'gp', score: 20 },
					{ answerId: 'lp', score: -100 },
					{ answerId: 'ci', score: 0 },
					{ answerId: 'n', score: 20 },
				],
			},
			{
				questionId: 'a',
				answers: [
					{ answerId: 'ap', score: 0 },
					{ answerId: 'eq', score: 20 },
				],
			},
			{
				questionId: 'br',
				answers: [
					{ answerId: 'ef', score: -10 },
					{ answerId: 'nef', score: 20 },
					{ answerId: 'ef2', score: 10 },
					{ answerId: 'pc', score: 10 },
				],
			},
			{
				questionId: 'pt',
				answers: [
					{ answerId: 'hp', score: 0 },
					{ answerId: 'lp', score: 10 },
				],
			},
			{
				questionId: 'hs',
				answers: [
					{ answerId: 'ns', score: 0 },
					{ answerId: 'ws', score: 20 },
				],
			},
		],
		description: [
			{
				text: translateInline({
					en: "setup using an actuary's services",
					nl: 'oprichten met tussenkomst van notaris',
				}),
			},
			{
				text: translateInline({
					en: 'at least two partners',
					nl: 'minimaal twee partners',
				}),
			},
			{
				text: translateInline({
					en: 'for entrepreneurs collaborating in a project',
					nl: 'voor ondernemers die gezamenlijk werken aan een project',
				}),
			},

			{
				text: translateInline({
					en: 'easy access and exit of members',
					nl: 'makkelijk in- en uitreden leden',
				}),
			},
			{
				text: translateInline({
					en: 'not personally liable for any debts',
					nl: 'niet persoonlijk aansprakelijk voor eventuele schulden',
				}),
			},
			{
				text: translateInline({
					en: 'possible tax relief schemes: private business ownership allowance, SME profit exemption, tax relief for new companies',
					nl: 'recht op zelfstandigenaftrek en mkb-winstvrijstelling, starters profiteren van startersaftrek',
				}),
			},
		],
	},
];

export const calculateAllScores = (
	givenAnswers: Map<string, string | null>,
) => {
	const answersRecord: Record<string, string> = {};

	// Convert Map to Record, filtering out null values
	givenAnswers.forEach((value, key) => {
		if (value) {
			answersRecord[key] = value;
		}
	});

	return results.map((result) => ({
		resultId: result.resultId,
		title: result.title,
		totalScore:
			result.scoreResult?.reduce((total, question) => {
				const userAnswer = answersRecord[question.questionId];
				const scoreEntry = question.answers.find(
					(answer) => answer.answerId === userAnswer,
				);
				return total + (scoreEntry?.score || 0);
			}, 0) || 0,
		description: result.description,
	}));
};

export const convertOldToNewUrlParams = (profileParam: string): string => {
	if (profileParam && typeof profileParam === 'string') {
		const param = profileParam.includes('profile=')
			? new URLSearchParams(profileParam.split('?')[1] || '').get('profile') ||
				''
			: profileParam;

		// Map question ID's
		const questionMapping: Record<string, string> = {
			C: 'at',
			A: 'a',
			B: 'br',
			R: 'pt',
			S: 'hs',
		};

		// Map answer ID's
		const answerMapping: Record<string, Record<string, string>> = {
			at: { a: 'a', p: 'p', g: 'gp', l: 'lp', i: 'ci', n: 'n' },
			a: { s: 'ap', e: 'eq' },
			br: { c: 'ef', h: 'nef', x: 'ef2', v: 'pc' },
			pt: { b: 'hp', m: 'lp' },
			hs: { o: 'ns', w: 'ws' },
		};

		const urlParams = new URLSearchParams();
		for (let i = 0; i < param.length; i += 2) {
			const questionChar = param.charAt(i);
			const answerChar = param.charAt(i + 1);

			const questionId = questionMapping[questionChar];

			if (questionId) {
				// If answer is unanswered (marked with '-'), add empty value
				if (answerChar === '-') {
					urlParams.set(questionId, '');
				} else if (answerMapping[questionId]) {
					const answerId = answerMapping[questionId][answerChar];

					if (answerId) {
						urlParams.set(questionId, answerId);
					}
				}
			}
		}

		return urlParams.toString();
	}

	return profileParam;
};
